import * as React from 'react';

import classNames from 'classnames';

import Imgix from 'js/components/Imgix';
import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';

import { StyleSheet, css } from '@coursera/coursera-ui';

import { getPartnerCTAData, getPartnerLogos } from 'bundles/front-page/constants/defaults';
import { sourceSansPro, stylusBreakpoints } from 'bundles/front-page/constants/rebrand';
import { TrackedA } from 'bundles/page/components/TrackedLink2';
import { isPartnersPageMigrationEnabled } from 'bundles/partners-graphql/utils/experiments';

const styles = StyleSheet.create({
  partnerLogosRoot: {
    fontFamily: sourceSansPro,
    textAlign: 'center',
    width: '100%',
    backgroundColor: '#F5F5F5',
    paddingTop: '64px',
    paddingBottom: '64px',
    margin: '0 auto',
    maxWidth: '100%',
    minHeight: 299,
    [stylusBreakpoints.tabletOrSmaller]: {
      minHeight: 358,
    },
  },
  title: {
    fontFamily: sourceSansPro,
    fontSize: '28px',
    fontWeight: 600,
    lineHeight: '36px',
    color: '#0056D2',
    textAlign: 'center',
    marginBottom: '50px',
    [stylusBreakpoints.tabletOrSmaller]: {
      fontSize: '20px',
      fontWeight: 400,
      lineHeight: '28px',
    },
    [stylusBreakpoints.phoneOrSmaller]: {
      padding: '0px 18px',
    },
  },
  logos: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    margin: '0 auto',
    maxWidth: '1132px',
    padding: '0 10px',
    width: '100%',
    '@media screen and (max-width: 780px)': {
      flexWrap: 'wrap',
      // set this way to make images (set by const) in flexbox layout look good
      maxWidth: '555px',
    },
    '@media screen and (max-width: 525px)': {
      maxWidth: '375px',
      paddingBottom: '15px',
    },
    '@media screen and (max-width: 374px)': {
      justifyContent: 'space-around',
    },
  },
  partnerImageLink: {
    display: 'inline-block',
    margin: '0 10px 30px',
  },
  logoImage: {
    width: '100%',
    '@media screen and (max-width: 780px)': {
      width: 'auto',
    },
  },
  adjustImgHeightTo27LargeScreen: {
    '@media screen and (max-width: 780px)': {
      height: '27px',
    },
  },
  adjustImgHeightTo27MdScreen: {
    '@media screen and (max-width: 525px)': {
      height: '27px',
    },
  },
  adjustImgHeightTo22MdScreen: {
    '@media screen and (max-width: 525px)': {
      height: '22px',
    },
  },
  adjustImgHeightTo17MdScreen: {
    '@media screen and (max-width: 525px)': {
      height: '17px',
    },
  },
  seeAllLink: {
    display: 'inline-block',
    color: '#000000',
    ':hover': {
      textDecoration: 'none',
      color: '#000000',
    },
  },
  focusPoint: {
    color: '#0056D2',
  },
  underline: {
    textDecoration: 'underline',
    ':hover': {
      textDecoration: 'underline',
    },
  },
});

type CTAdata = {
  href: string;
  trackingName: string;
  data: { value: string };
  className: string;
  message: string;
  rebrandMessage: (className: string) => string;
};

type State = {
  isBeingHovered: boolean;
};

class CTALink extends React.Component<CTAdata, State> {
  state = {
    isBeingHovered: false,
  };

  render() {
    const { href, trackingName, data, rebrandMessage } = this.props;
    const { isBeingHovered } = this.state;
    const taglineClasses = css(styles.focusPoint, isBeingHovered && styles.underline).className;

    return (
      <div>
        <TrackedA
          className={css(styles.seeAllLink).className}
          href={href}
          trackingName={trackingName}
          data={data}
          onMouseOver={() => this.setState(() => ({ isBeingHovered: true }))}
          onFocus={() => this.setState(() => ({ isBeingHovered: true }))}
          onMouseLeave={() => this.setState(() => ({ isBeingHovered: false }))}
        >
          <FormattedHTMLMessage message={rebrandMessage(taglineClasses)} tagName="span" />
        </TrackedA>
      </div>
    );
  }
}

const renderPartnerLogoImages = () => {
  const logoImages = getPartnerLogos();
  return logoImages.map((partner) => {
    const isGoogleIllinoisOrDuke = partner.id === 'google' || partner.id === 'illinois' || partner.id === 'duke';
    const isImperialOrPenn = partner.id === 'imperial' || partner.id === 'penn';
    const isIbmOrStanford = partner.id === 'ibm' || partner.id === 'stanford';

    const adjustImgHeightTo27LgClassname = css(styles.adjustImgHeightTo27LargeScreen).className;
    const adjustImgHeightTo27MdClassname = css(styles.adjustImgHeightTo27MdScreen).className;
    const adjustImgHeightTo22MdClassname = css(styles.adjustImgHeightTo22MdScreen).className;
    const adjustImgHeightTo17MdClassname = css(styles.adjustImgHeightTo17MdScreen).className;

    const imgClasses = classNames(css(styles.logoImage).className, {
      [adjustImgHeightTo27LgClassname]: isGoogleIllinoisOrDuke,
      [adjustImgHeightTo27MdClassname]: partner.id === 'umich',
      [adjustImgHeightTo22MdClassname]: isImperialOrPenn || isGoogleIllinoisOrDuke,
      [adjustImgHeightTo17MdClassname]: isIbmOrStanford,
    });

    return (
      <a
        {...css(styles.partnerImageLink)}
        href={isPartnersPageMigrationEnabled() ? `/partners/${partner.id}` : `/${partner.id}`}
        data-id={partner.id}
        key={partner.id}
      >
        <Imgix className={imgClasses} alt={partner.title} src={partner.image} maxHeight={partner.height} />
      </a>
    );
  });
};

const PartnerLogosRedesign = () => {
  const partnerCtaData = getPartnerCTAData();
  return (
    <section className={classNames('rc-PartnerLogosRedesign', css(styles.partnerLogosRoot).className)}>
      <h2 {...css(styles.title)}>
        <CTALink {...partnerCtaData} />
      </h2>
      <div {...css(styles.logos)}>{renderPartnerLogoImages()}</div>
    </section>
  );
};

export default PartnerLogosRedesign;
